import React from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  KeyboardArrowDown,
  AutoAwesome,
  Chat,
  Lightbulb
} from '@mui/icons-material';
import useAbeStore, { AbeView } from '../../store/zustand/abe';
import AbeChat from './AbeChat';
import AbeIdeasMenu from './AbeIdeasMenu';
import { mainColors } from '../../theme/themeConfig';
// eslint-disable-next-line import/no-named-as-default
import useAbe from '../../hooks/useAbe';
import useAuthStore from '../../store/zustand/auth';

const DRAWER_WIDTH = 400;

function AbeWidget(): JSX.Element | null {
  const roles = useAuthStore((s) => s.roles);
  const isOpen = useAbeStore((s) => s.isOpen);
  const view = useAbeStore((s) => s.view);
  const { openAbe, closeAbe, setView } = useAbeStore();
  const { sendMessage } = useAbe();

  const OPERATOR_ROLES = [
    'Operations',
    'Administrator',
    'SuperUser',
    'Supervisor',
    'BusinessDevelopment',
    'Sales',
    'Finance'
  ];
  const hasOperatorRole = (roles as string[])?.some((r) =>
    OPERATOR_ROLES.includes(r)
  );
  if (!hasOperatorRole) return null;

  const handleSelectIdea = (prompt: string) => {
    setView('chat');
    sendMessage(prompt);
  };

  const handleTabChange = (_: React.SyntheticEvent, newView: AbeView) => {
    setView(newView);
  };

  return (
    <>
      {/* Floating ASK ABE trigger — always visible when drawer is closed */}
      {!isOpen && (
        <Tooltip placement="left" title="Open Ask Abe">
          <Box
            onClick={openAbe}
            sx={{
              '&:hover': {
                bgcolor: mainColors.MAIN_5,
                boxShadow: '0 6px 20px rgba(0,93,162,0.5)',
                transform: 'translateY(-2px)'
              },
              alignItems: 'center',
              bgcolor: mainColors.MAIN,
              borderRadius: '28px',
              bottom: 24,
              boxShadow: '0 4px 16px rgba(0,93,162,0.4)',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              gap: 1,
              position: 'fixed',
              px: 2,
              py: 1,
              right: 24,
              transition: 'all 0.2s',
              userSelect: 'none',
              zIndex: 1300
            }}
          >
            <AutoAwesome sx={{ fontSize: 18 }} />
            <Typography
              sx={{
                color: '#fff',
                fontSize: '0.8rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                lineHeight: 1
              }}
            >
              ASK ABE
            </Typography>
          </Box>
        </Tooltip>
      )}

      {/* Side drawer — temporary so it overlays content instead of pushing it */}
      <Drawer
        ModalProps={{ keepMounted: true, sx: { zIndex: 2000 } }}
        PaperProps={{
          sx: {
            borderRadius: '12px 0 0 12px',
            boxShadow: '-4px 0 32px rgba(0,0,0,0.18)',
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '95vw',
            overflow: 'hidden',
            width: DRAWER_WIDTH
          }
        }}
        anchor="right"
        onClose={closeAbe}
        open={isOpen}
        variant="temporary"
      >
        {/* Header */}
        <Box
          sx={{
            alignItems: 'center',
            bgcolor: mainColors.MAIN,
            display: 'flex',
            flexShrink: 0,
            justifyContent: 'space-between',
            px: 2,
            py: 1.5
          }}
        >
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1.25 }}>
            <AutoAwesome sx={{ color: '#fff', fontSize: 18 }} />
            <Box>
              <Typography
                sx={{
                  color: '#fff',
                  fontSize: '0.85rem',
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  lineHeight: 1.2
                }}
              >
                ASK ABE
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: '0.65rem',
                  letterSpacing: '0.04em',
                  lineHeight: 1
                }}
              >
                AI Business Engine
              </Typography>
            </Box>
          </Box>

          {/* Minimize — collapses drawer back to the ASK ABE pill */}
          <Tooltip title="Minimize">
            <IconButton
              onClick={closeAbe}
              size="small"
              sx={{
                '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
                bgcolor: 'rgba(255,255,255,0.12)',
                color: '#fff',
                height: 28,
                width: 28
              }}
            >
              <KeyboardArrowDown fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Tabs: Chat | Ideas */}
        <Tabs
          onChange={handleTabChange}
          sx={{
            '& .Mui-selected': { color: mainColors.MAIN },
            '& .MuiTab-root': {
              fontSize: '0.78rem',
              fontWeight: 500,
              minHeight: 40,
              py: 0.5,
              textTransform: 'none'
            },
            '& .MuiTabs-indicator': { bgcolor: mainColors.MAIN },
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            minHeight: 40
          }}
          value={view}
        >
          <Tab
            icon={<Chat sx={{ fontSize: 15 }} />}
            iconPosition="start"
            label="Chat"
            value="chat"
          />
          <Tab
            icon={<Lightbulb sx={{ fontSize: 15 }} />}
            iconPosition="start"
            label="Ideas Menu"
            value="ideas"
          />
        </Tabs>

        {/* Content */}
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
          {view === 'chat' ? (
            <AbeChat />
          ) : (
            <AbeIdeasMenu onSelectIdea={handleSelectIdea} />
          )}
        </Box>
      </Drawer>
    </>
  );
}

export default AbeWidget;
